import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BadgeCheck,
  Boxes,
  PlugZap,
  Calendar,
  CheckCircle2,
  FileCode2,
  Fingerprint,
  PackagePlus,
  Plug,
  PlusCircle,
  Settings2,
  Sparkles,
  XCircle,
  Store,
  Trash2,
} from "lucide-react";
import { Card, CardBody } from "../components/ui/Card";
import { Badge, StatusBadge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/Spinner";
import { Field, Input, Textarea } from "../components/ui/Input";
import { ConfigForm } from "../components/connector/ConfigForm";
import { useAuth } from "../lib/auth";
import { useConnection, useConnectionConfig } from "../lib/hooks";
import {
  ApiError,
  addModules,
  deleteConnection,
  publishConnection,
  testConnection,
  waitForJob,
} from "../lib/api";
import type { ConnectionTestResult, ExchangeListing, GenerationJob } from "../lib/api";
import { cn } from "../lib/cn";

function Meta({ icon: Icon, label, value }: { icon: typeof Plug; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3">
      <Icon className="h-4 w-4 text-slate-400" />
      <div className="min-w-0">
        <p className="text-xs text-slate-400">{label}</p>
        <p className="truncate text-sm font-medium text-slate-700">{value}</p>
      </div>
    </div>
  );
}

function PublishModal({
  open,
  onClose,
  tenant,
  connectionSlug,
  onPublished,
}: {
  open: boolean;
  onClose: () => void;
  tenant: string;
  connectionSlug: string;
  onPublished: (l: ExchangeListing) => void;
}) {
  const { user } = useAuth();
  const canCurate = !!user?.is_platform_admin;
  const [listingSlug, setListingSlug] = useState(connectionSlug);
  const [description, setDescription] = useState("");
  const [official, setOfficial] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const listing = await publishConnection(tenant, {
        connection_slug: connectionSlug,
        listing_slug: listingSlug || undefined,
        description,
        ...(canCurate && official ? { official: true } : {}),
      });
      onPublished(listing);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't reach the server");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Publish to Connector Exchange">
      <form onSubmit={onSubmit}>
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm text-slate-500">
            Publishing shares this connector's <span className="font-medium text-slate-700">code
            bundle, capabilities and parameter schema</span> with every workspace. Configured
            values and secrets are <span className="font-medium text-slate-700">never</span> shared.
          </p>
          {error && (
            <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 ring-1 ring-inset ring-rose-100">
              {error}
            </div>
          )}
          <Field label="Exchange slug" hint="Globally unique identifier on the exchange.">
            <Input
              value={listingSlug}
              onChange={(e) => setListingSlug(e.target.value)}
              pattern="[a-z0-9][a-z0-9\-]*"
              required
            />
          </Field>
          <Field label="Description" hint="Help other teams decide — what does it connect, which auth?">
            <Textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Salesforce REST connector with OAuth2 client-credentials…"
            />
          </Field>
          {canCurate && (
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
              <input
                type="checkbox"
                checked={official}
                onChange={(e) => setOfficial(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span>
                <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                  <BadgeCheck className="h-4 w-4 text-brand-500" /> Publish to the Wise Exchange
                </span>
                <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">
                  Lists it as an official, maintained connector — shown first to every workspace
                  and kept even if this workspace is deleted.
                </span>
              </span>
            </label>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            <Store className="h-4 w-4" /> Publish
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function AddModuleModal({
  open,
  onClose,
  tenant,
  connectionSlug,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  tenant: string;
  connectionSlug: string;
  onDone: () => void;
}) {
  const [spec, setSpec] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<GenerationJob | null>(null);
  const abort = useRef<AbortController | null>(null);

  // Generation outlives the request, so stop polling if the modal goes away.
  useEffect(() => () => abort.current?.abort(), []);

  function close() {
    abort.current?.abort();
    setSpec("");
    setError(null);
    setJob(null);
    setBusy(false);
    onClose();
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const ctrl = new AbortController();
    abort.current = ctrl;
    try {
      // 202 + poll: extending a connector is a multi-minute AI + sandbox run.
      const started = await addModules(tenant, connectionSlug, spec);
      setJob(started);
      const final = await waitForJob(tenant, started.id, setJob, ctrl.signal);
      if (final.status === "succeeded") {
        onDone();
      } else {
        setError(final.error || "Generation didn't complete.");
        setJob(null);
      }
    } catch (err) {
      if (ctrl.signal.aborted) return;
      setError(err instanceof ApiError ? err.message : "Couldn't reach the server");
      setJob(null);
    } finally {
      if (!ctrl.signal.aborted) setBusy(false);
    }
  }

  const succeeded = job?.status === "succeeded" && job.connection;

  return (
    <Modal open={open} onClose={busy ? () => {} : close} title="Add modules by chat">
      {succeeded && job?.connection ? (
        <>
          <div className="space-y-4 px-6 py-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600">
              <CheckCircle2 className="h-5 w-5" /> Modules added — connector re-tested in the sandbox
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Capabilities now</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {job.connection.capabilities.map((cap) => (
                  <Badge key={cap} tone="brand" className="font-mono text-[11px]">{cap}</Badge>
                ))}
              </div>
            </div>
            {job.codegen_notes && (
              <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <span className="font-semibold">Notes:</span> {job.codegen_notes}
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
            <Button onClick={close}>Done</Button>
          </div>
        </>
      ) : (
        <form onSubmit={onSubmit}>
          <div className="space-y-4 px-6 py-5">
            <p className="text-sm text-slate-500">
              Describe the modules/operations to add — the AI checks them against what this
              system's API supports, extends the connector, and re-tests it in the sandbox.
              Existing capabilities are never removed.
            </p>
            {error && (
              <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 ring-1 ring-inset ring-rose-100">
                {error}
              </div>
            )}
            <Field
              label="What should this connector also do?"
              hint='e.g. "Add an accounts module with query, create and update."'
            >
              <Textarea
                rows={3}
                required
                value={spec}
                onChange={(e) => setSpec(e.target.value)}
                placeholder="Add an invoices module with query and create…"
              />
            </Field>
            {busy && (
              <div className="rounded-xl bg-brand-50/70 px-4 py-3 text-sm text-brand-800 ring-1 ring-inset ring-brand-100">
                <div className="flex items-center gap-2.5">
                  <Spinner className="h-4 w-4" />
                  {job?.phase || "Starting…"}
                </div>
                <div className="mt-2.5 h-1 overflow-hidden rounded-full bg-brand-100">
                  <div
                    className="h-full rounded-full bg-brand-500 transition-[width] duration-700 ease-out"
                    style={{ width: `${Math.max(job?.progress ?? 0, 4)}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-brand-700/70">
                  Runs on the server — usually 1–3 minutes.
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
            <Button type="button" variant="ghost" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" loading={busy}>
              <Sparkles className="h-4 w-4" /> Add modules
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/** Check the saved credentials against the real system.
 *
 * The connector runs in the sandbox, not here — this only shows the verdict. */
function TestConnectionCard({
  tenant,
  slug,
  hasCode,
  onTested,
}: {
  tenant: string;
  slug: string;
  hasCode: boolean;
  onTested: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ConnectionTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const outcome = await testConnection(tenant, slug);
      setResult(outcome);
      onTested();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't reach the server.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardBody>
        <div className="flex items-center gap-2.5">
          <PlugZap className="h-5 w-5 text-brand-600" />
          <h2 className="text-sm font-semibold text-slate-900">Connection test</h2>
        </div>
        <p className="mt-1.5 text-xs leading-relaxed text-slate-400">
          {hasCode
            ? "Runs this connector against your system in the gVisor sandbox using the saved credentials."
            : "This connection has no connector code yet, so there is nothing to run."}
        </p>

        <Button
          size="sm"
          variant="outline"
          className="mt-4 w-full"
          loading={busy}
          disabled={!hasCode}
          onClick={run}
        >
          <PlugZap className="h-4 w-4" /> Test connection
        </Button>

        {error && (
          <div className="mt-3 rounded-xl bg-rose-50 px-3.5 py-2.5 text-xs text-rose-700 ring-1 ring-inset ring-rose-100">
            {error}
          </div>
        )}

        {result && (
          <div
            className={cn(
              "mt-3 rounded-xl px-3.5 py-3 text-xs ring-1 ring-inset",
              result.ok
                ? "bg-emerald-50 text-emerald-800 ring-emerald-100"
                : "bg-rose-50 text-rose-700 ring-rose-100",
            )}
          >
            <p className="flex items-center gap-1.5 font-semibold">
              {result.ok ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Connected
                  {result.latency_ms != null && (
                    <span className="font-normal opacity-70">· {result.latency_ms} ms</span>
                  )}
                </>
              ) : (
                <>
                  <XCircle className="h-3.5 w-3.5" /> Connection failed
                </>
              )}
            </p>
            <p className="mt-1 leading-relaxed">{result.detail}</p>
            {result.trace && (
              <pre className="mt-2 max-h-32 overflow-auto rounded-lg bg-white/60 p-2 text-[10px] leading-relaxed">
                {result.trace.trim()}
              </pre>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default function ConnectionDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { currentTenant } = useAuth();
  const navigate = useNavigate();
  const { data: c, loading, error, reload } = useConnection(currentTenant?.slug, slug);
  const cfg = useConnectionConfig(currentTenant?.slug, slug);
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [published, setPublished] = useState<ExchangeListing | null>(null);
  const [moduleOpen, setModuleOpen] = useState(false);

  async function onDelete() {
    if (!currentTenant || !slug) return;
    setDeleting(true);
    try {
      await deleteConnection(currentTenant.slug, slug);
      navigate("/app/connections");
    } catch {
      setDeleting(false);
      setConfirm(false);
    }
  }

  return (
    <>
      <Link to="/app/connections" className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Connections
      </Link>

      {loading ? (
        <div className="flex justify-center py-20">
          <Spinner className="h-8 w-8 text-brand-500" />
        </div>
      ) : error || !c ? (
        <Card className="px-6 py-16 text-center">
          <p className="font-semibold text-slate-800">Connection not found</p>
          <p className="mt-1 text-sm text-slate-400">{error ?? "It may have been deleted."}</p>
        </Card>
      ) : (
        <>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-lift">
                <Plug className="h-7 w-7" />
              </span>
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">{c.display_name}</h1>
                  <StatusBadge status={c.status} />
                  {published && (
                    <Badge tone="violet">
                      <Store className="h-3 w-3" /> Published v{published.version}
                    </Badge>
                  )}
                </div>
                <p className="mt-0.5 text-sm text-slate-400">
                  {c.system_type} · <span className="font-mono text-xs">{c.slug}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <Button variant="outline" onClick={() => setPublishOpen(true)}>
                <Store className="h-4 w-4 text-brand-600" /> Publish
              </Button>
              <Button variant="outline" onClick={() => setConfirm(true)}>
                <Trash2 className="h-4 w-4 text-rose-500" /> Delete
              </Button>
            </div>
          </div>

          {c.status === "installed" && (
            <div className="mt-6 flex items-start gap-3 rounded-2xl bg-accent-50/80 px-5 py-4 ring-1 ring-inset ring-accent-100">
              <PackagePlus className="mt-0.5 h-5 w-5 shrink-0 text-accent-600" />
              <div>
                <p className="text-sm font-semibold text-accent-900">Installed from the Connector Exchange</p>
                <p className="mt-0.5 text-sm text-accent-700">
                  Finish setup below — fill in this workspace's own endpoints and credentials.
                </p>
              </div>
            </div>
          )}

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Meta icon={Boxes} label="System type" value={c.system_type} />
            <Meta icon={Calendar} label="Created" value={new Date(c.created_at).toLocaleDateString()} />
            <Meta icon={FileCode2} label="Config version" value={`v${c.xml_version}`} />
            <Meta icon={Fingerprint} label="Bundle" value={c.bundle_sha256 ? c.bundle_sha256.slice(0, 12) : "—"} />
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardBody>
                <div className="flex items-center gap-2.5">
                  <Settings2 className="h-5 w-5 text-brand-600" />
                  <h2 className="text-base font-semibold text-slate-900">Configuration</h2>
                </div>
                <p className="mt-1 text-sm text-slate-400">
                  Parameters this connector needs to reach your {c.system_type} system.
                </p>
                <div className="mt-6">
                  {cfg.loading ? (
                    <div className="flex justify-center py-10">
                      <Spinner className="h-6 w-6 text-brand-500" />
                    </div>
                  ) : cfg.error || !cfg.data ? (
                    <p className="py-6 text-sm text-slate-400">{cfg.error ?? "Couldn't load configuration."}</p>
                  ) : (
                    <ConfigForm
                      tenant={currentTenant!.slug}
                      slug={c.slug}
                      systemType={c.system_type}
                      config={cfg.data}
                      onSaved={() => {
                        cfg.reload();
                        reload();
                      }}
                    />
                  )}
                </div>
              </CardBody>
            </Card>

            <div className="space-y-6">
              <TestConnectionCard
                tenant={currentTenant!.slug}
                slug={c.slug}
                hasCode={!!c.bundle_sha256}
                onTested={reload}
              />

              <Card>
                <CardBody>
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-slate-900">Modules & operations</h2>
                    {c.bundle_sha256 && (
                      <button
                        type="button"
                        onClick={() => setModuleOpen(true)}
                        className="focus-ring inline-flex items-center gap-1 rounded-lg text-sm font-semibold text-brand-600 hover:text-brand-700"
                      >
                        <PlusCircle className="h-4 w-4" /> Add module
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-slate-400">Exposed to the agent as tools.</p>
                  {c.capabilities.length === 0 ? (
                    <p className="mt-4 text-sm text-slate-400">No capabilities declared.</p>
                  ) : (
                    <ul className="mt-4 space-y-2">
                      {c.capabilities.map((cap) => (
                        <li key={cap} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-2.5">
                          <code className="text-sm font-medium text-brand-700">{cap}</code>
                          <span className="text-xs text-slate-400">tool</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <h2 className="text-sm font-semibold text-slate-900">Signed config</h2>
                  <div className="mt-4 space-y-3 text-sm">
                    <div>
                      <p className="text-xs text-slate-400">Object key</p>
                      <p className="mt-0.5 break-all font-mono text-xs text-slate-600">{c.xml_object_key}</p>
                    </div>
                    <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-3.5 py-2.5 text-xs text-slate-500">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      Ed25519-signed · secrets stored as Vault references
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </>
      )}

      {currentTenant && slug && (
        <>
          <PublishModal
            open={publishOpen}
            onClose={() => setPublishOpen(false)}
            tenant={currentTenant.slug}
            connectionSlug={slug}
            onPublished={setPublished}
          />
          <AddModuleModal
            open={moduleOpen}
            onClose={() => setModuleOpen(false)}
            tenant={currentTenant.slug}
            connectionSlug={slug}
            onDone={() => {
              reload();
              cfg.reload();
            }}
          />
        </>
      )}

      <Modal open={confirm} onClose={() => setConfirm(false)} title="Delete connection?">
        <div className="px-6 py-5">
          <p className="text-sm text-slate-600">
            This removes <span className="font-semibold">{c?.display_name}</span> and its stored config. This
            can't be undone.
          </p>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <Button variant="ghost" onClick={() => setConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={deleting} onClick={onDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
}
